import { calculatePdfFit, type ViewerPosition } from '@ironview/interactive-viewer';

export interface AreaFocusSize {
  width: number;
  height: number;
}

export interface AreaFocusViewBox extends AreaFocusSize {
  x: number;
  y: number;
}

export interface AreaFocusViewport extends AreaFocusSize {
  centerX: number;
  centerY: number;
}

export interface PdfOverviewFit {
  scale: number;
  position: ViewerPosition;
}

export interface ResolveAreaFocusViewportOptions {
  isPdfLayout: boolean;
  pdfViewportRect?: AreaFocusSize | null;
  overlayRect?: AreaFocusSize | null;
  viewBox?: AreaFocusViewBox | null;
  fallback: AreaFocusSize;
}

const usableSize = (
  value: AreaFocusSize | null | undefined
): value is AreaFocusSize =>
  Boolean(
    value &&
      Number.isFinite(value.width) &&
      Number.isFinite(value.height) &&
      value.width > 0 &&
      value.height > 0
  );

export const resolveAreaFocusViewport = ({
  isPdfLayout,
  pdfViewportRect,
  overlayRect,
  viewBox,
  fallback,
}: ResolveAreaFocusViewportOptions): AreaFocusViewport => {
  if (isPdfLayout) {
    const viewport = usableSize(pdfViewportRect)
      ? pdfViewportRect
      : usableSize(overlayRect)
        ? overlayRect
        : fallback;
    return {
      width: viewport.width,
      height: viewport.height,
      centerX: viewport.width / 2,
      centerY: viewport.height / 2,
    };
  }

  const viewport = usableSize(viewBox)
    ? viewBox
    : usableSize(overlayRect)
      ? overlayRect
      : fallback;
  const originX = viewBox && usableSize(viewBox) ? viewBox.x : 0;
  const originY = viewBox && usableSize(viewBox) ? viewBox.y : 0;
  return {
    width: viewport.width,
    height: viewport.height,
    centerX: originX + viewport.width / 2,
    centerY: originY + viewport.height / 2,
  };
};

export const resolvePdfOverviewFit = (
  viewport: AreaFocusSize | null | undefined,
  content: AreaFocusSize | null | undefined,
  fallback: PdfOverviewFit | null
): PdfOverviewFit | null => {
  if (!usableSize(viewport) || !usableSize(content)) {
    return fallback;
  }
  return calculatePdfFit(viewport, content);
};
